import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import { Menu, X, Church } from 'lucide-react';
import '@/App.css';

import Home from './pages/Home';
import About from './pages/About';
import Events from './pages/Events';
import Giving from './pages/Giving';
import Gallery from './pages/Gallery';
import Contact from './pages/Contact';
import Auth from './pages/Auth';
import AdminDashboard from './pages/AdminDashboard';
import PrayerRequest from './pages/PrayerRequest';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import { SiteSettingsProvider, useSiteSettings } from './context/SiteSettingsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export { API };

function Navigation({ user, onLogout }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    onLogout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  return (
    <nav className="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center space-x-3" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_coosbaychurch/artifacts/zfdqhplq_Coosbay%20logo%20plain.jpg" 
              alt="Coos Bay Cornerstone Logo" 
              className="h-20 w-auto"
            />
            <span className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif', color: '#1e3a8a' }}>Coos Bay Cornerstone</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="nav-link" data-testid="nav-home">Home</Link>
            <Link to="/about" className="nav-link" data-testid="nav-about">About</Link>
            <Link to="/events" className="nav-link" data-testid="nav-events">Events</Link>
            <Link to="/giving" className="nav-link" data-testid="nav-giving">Giving</Link>
            <Link to="/media" className="nav-link" data-testid="nav-media">Media</Link>
            <Link to="/prayer" className="nav-link" data-testid="nav-prayer">Prayer</Link>
            <Link to="/contact" className="nav-link" data-testid="nav-contact">Contact</Link>
            
            {user ? (
              <>
                {user.is_admin && (
                  <Link to="/admin" className="btn-secondary" data-testid="nav-admin">Admin</Link>
                )}
                <button onClick={handleLogout} className="btn-primary" data-testid="logout-btn">Logout</button>
              </>
            ) : (
              <Link to="/auth" className="btn-primary" data-testid="nav-login">Login</Link>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X size={28} color="#1e3a8a" /> : <Menu size={28} color="#1e3a8a" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden pb-6 space-y-4" data-testid="mobile-menu">
            <Link to="/" className="block nav-link py-2" onClick={() => setMobileMenuOpen(false)}>Home</Link>
            <Link to="/about" className="block nav-link py-2" onClick={() => setMobileMenuOpen(false)}>About</Link>
            <Link to="/events" className="block nav-link py-2" onClick={() => setMobileMenuOpen(false)}>Events</Link>
            <Link to="/giving" className="block nav-link py-2" onClick={() => setMobileMenuOpen(false)}>Giving</Link>
            <Link to="/media" className="block nav-link py-2" onClick={() => setMobileMenuOpen(false)}>Media</Link>
            <Link to="/prayer" className="block nav-link py-2" onClick={() => setMobileMenuOpen(false)}>Prayer</Link>
            <Link to="/contact" className="block nav-link py-2" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
            
            {user ? (
              <>
                {user.is_admin && (
                  <Link to="/admin" className="block btn-secondary text-center" onClick={() => setMobileMenuOpen(false)}>Admin</Link>
                )}
                <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="btn-primary w-full">Logout</button>
              </>
            ) : (
              <Link to="/auth" className="block btn-primary text-center" onClick={() => setMobileMenuOpen(false)}>Login</Link>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}

function Footer() {
  const { settings } = useSiteSettings();
  
  return (
    <footer className="bg-blue-900 text-white py-12 mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center md:text-left">
          <div className="flex flex-col items-center md:items-start">
            <div className="bg-white rounded-xl p-4 mb-4">
              <img 
                src={settings.logo_url || "https://customer-assets.emergentagent.com/job_coosbaychurch/artifacts/zfdqhplq_Coosbay%20logo%20plain.jpg"}
                alt={`${settings.church_name} Logo`}
                className="h-28 w-auto"
              />
            </div>
            <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>{settings.church_name}</h3>
            <p className="text-lg">{settings.welcome_text?.split('.')[0] || 'Loving God and Others'}</p>
          </div>
          <div>
            <h4 className="text-xl font-semibold mb-4">Contact</h4>
            <p className="text-lg mb-2">{settings.address}</p>
            <p className="text-lg mb-2">{settings.city_state_zip}</p>
            <p className="text-lg">{settings.phone}</p>
          </div>
          <div>
            <h4 className="text-xl font-semibold mb-4">Service Times</h4>
            <p className="text-lg mb-2">Sunday School: {settings.sunday_school_time}</p>
            <p className="text-lg">Sunday Service: {settings.sunday_service_time}</p>
          </div>
          <div>
            <h4 className="text-xl font-semibold mb-4">Quick Links</h4>
            <div className="space-y-2">
              <Link to="/events" className="block text-lg hover:text-amber-300 transition-colors">Events</Link>
              <Link to="/prayer" className="block text-lg hover:text-amber-300 transition-colors">Prayer Requests</Link>
              <Link to="/giving" className="block text-lg hover:text-amber-300 transition-colors">Give Online</Link>
              <Link to="/contact" className="block text-lg hover:text-amber-300 transition-colors">Contact Us</Link>
            </div>
          </div>
        </div>
        <div className="mt-8 pt-8 border-t border-blue-800 text-center">
          <p className="text-lg mb-4">{settings.footer_text}</p>
          <div className="flex justify-center space-x-6 text-sm">
            <Link to="/privacy-policy" className="hover:text-amber-300 transition-colors" data-testid="privacy-policy-link">
              Privacy Policy
            </Link>
            <span className="text-blue-400">|</span>
            <Link to="/terms-of-service" className="hover:text-amber-300 transition-colors" data-testid="terms-of-service-link">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function App() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  return (
    <SiteSettingsProvider>
      <div className="App">
        <Toaster position="top-center" richColors />
        <BrowserRouter>
          <Navigation user={user} onLogout={() => setUser(null)} />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/events" element={<Events user={user} />} />
            <Route path="/giving" element={<Giving />} />
            <Route path="/media" element={<Gallery />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/prayer" element={<PrayerRequest />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/auth" element={<Auth setUser={setUser} />} />
            <Route path="/admin" element={<AdminDashboard user={user} />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
          </Routes>
          <Footer />
        </BrowserRouter>
      </div>
    </SiteSettingsProvider>
  );
}

export default App;